import * as Sentry from "@sentry/react";

let initialised = false;

export function initSentry() {
  if (initialised) return;
  // Publishable DSN — safe to commit (Sentry DSNs are designed to be public).
  const dsn =
    (import.meta.env.VITE_SENTRY_DSN as string | undefined) ||
    "https://6197bc98640cb31d277ca14e8b860edb@o4511599495413760.ingest.de.sentry.io/4511599816933456";
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // Drop events from internal Lovable test accounts.
      const email = event.user?.email;
      if (email && /@lovable\.dev$/i.test(email)) return null;

      // Scrub Supabase access tokens that may have ended up in breadcrumbs.
      if (event.breadcrumbs) {
        for (const b of event.breadcrumbs) {
          if (b.data && typeof b.data === "object") {
            for (const k of Object.keys(b.data)) {
              const v = (b.data as Record<string, unknown>)[k];
              if (typeof v === "string" && /eyJ[A-Za-z0-9_-]{20,}\./.test(v)) {
                (b.data as Record<string, unknown>)[k] = "[redacted-jwt]";
              }
            }
          }
        }
      }
      return event;
    },
  });

  initialised = true;
}

export function setSentryUser(user: {
  id: string;
  company_id?: string | null;
  is_admin?: boolean;
  is_company_admin?: boolean;
} | null) {
  if (!initialised) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  // Deliberately no email / name — id + role + company only.
  Sentry.setUser({
    id: user.id,
    segment: user.is_admin ? "admin" : user.is_company_admin ? "company_admin" : "user",
    company_id: user.company_id ?? undefined,
  });
}

export { Sentry };
